import React from "react";
import { Edit, Trash2, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Offer } from "@/types/offer";
import { formatDateRange } from "@/utils/dateUtils";
import { SortableOption } from "@/types/fieldsOptions";

// Function to get program display without color
const getProgramDisplay = (offer: Offer) => {
  return (
    <div>
      <span>{offer.variationData?.displayTitle || offer.variationKey}</span>
    </div>
  );
};

// Function to get the status label from an option object or fallback to ID
const getStatusLabel = (option?: SortableOption, fallbackId?: string): string => {
  if (option && option.label) {
    return option.label;
  }
  if (option && option.shortLabel) {
    return option.shortLabel;
  }
  return fallbackId || "Not specified";
};

const formatGrade = (key: string | undefined): string => {
  if (!key || key === "none") return "–";
  return key.replace(/^g_/, "").toUpperCase();
};

export interface CreateColumnsOptions {
  showLocationColumn?: boolean;
  getLocationName?: (offer: Offer) => string;
  /** When true, add min/max age, grade, tier 2 price, session length columns (org Sessions tab only). */
  showOrgExtraColumns?: boolean;
}

export const createColumns = (
  onEditOffer: (offer: Offer) => void,
  onDeleteOffer: (offerId: string, e: React.MouseEvent) => void,
  onCopyOffer?: (offerId: string) => void,
  options?: CreateColumnsOptions
) => [
  ...(options?.showLocationColumn && options?.getLocationName
    ? [
        {
          key: "location" as const,
          header: "Location",
          sortable: true,
          width: "w-[160px] min-w-[120px]",
          className: "p-2",
          render: (offer: Offer) => (
            <span className="text-muted-foreground">
              {options.getLocationName!(offer) || "–"}
            </span>
          ),
        },
      ]
    : []),
  {
    key: "displayTitle" as keyof Offer,
    header: "Session title",
    sortable: true,
    width: "w-[200px] min-w-[150px]",
    className: "whitespace-normal",
    render: (offer: Offer) => {
      const title = offer.displayTitle || "–";
      return (
        <div onClick={(e) => e.stopPropagation()}>
          {title !== "–" ? (
            <button 
              className="text-left text-blue-600 hover:underline focus:outline-none break-words"
              onClick={() => onEditOffer(offer)}
            >
              {title}
            </button>
          ) : (
            <div className="space-y-1">
              <span className="text-muted-foreground">–</span>
              <div>
                <button 
                  className="text-xs text-blue-600 hover:underline focus:outline-none"
                  onClick={() => onEditOffer(offer)}
                >
                  Edit
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }
  },
  {
    key: "variationKey" as keyof Offer,
    header: "Program",
    sortable: true,
    width: "w-[150px] min-w-[120px]",
    className: "p-2",
    render: (offer: Offer) => getProgramDisplay(offer)
  },
  {
    key: "startDate" as keyof Offer,
    header: "Dates",
    sortable: true,
    width: "w-[160px] min-w-[100px]",
    className: "whitespace-normal p-2",
    render: (offer: Offer) => {
      // Use the formatDateRange utility for consistent date formatting
      return (
        <span className="break-words">
          {formatDateRange(offer.startDate, offer.endDate)}
        </span>
      );
    }
  },
  {
    key: "priceCalc" as keyof Offer,
    header: "Price",
    sortable: true,
    width: "w-[80px] min-w-[50px]",
    className: "p-2",
    render: (offer: Offer) => {
      // priceCalc is already the derived value that follows the priority rules
      const price = offer.priceCalc ?? offer.priceRegular ?? offer.variationData?.priceRegular ?? 0;
      return <span>${price}</span>;
    }
  },
  {
    key: "availabilityStatus" as keyof Offer,
    header: "Full?",
    sortable: true,
    width: "w-[100px] min-w-[50px]",
    className: "whitespace-normal p-2",
    render: (offer: Offer) => {
      return <span>{getStatusLabel(offer.availabilityStatusOption, offer.availabilityStatus)}</span>;
    }
  },
  {
    key: "signupStatus" as keyof Offer,
    header: "Reg. open?",
    sortable: true,
    width: "w-[100px] min-w-[50px]",
    className: "whitespace-normal p-2",
    render: (offer: Offer) => {
      return <span>{getStatusLabel(offer.signupStatusOption, offer.signupStatus)}</span>;
    }
  },
  {
    key: "actions" as "actions",
    header: "Actions",
    sortable: false,
    width: "w-[90px] min-w-[50px]",
    className: "p-2",
    render: (offer: Offer) => (
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onEditOffer(offer);
          }}
        >
          <Edit className="h-4 w-4" />
          <span className="sr-only">Edit</span>
        </Button>
        {onCopyOffer && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onCopyOffer(offer.id);
            }}
          >
            <Copy className="h-4 w-4" />
            <span className="sr-only">Copy</span>
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => onDeleteOffer(offer.id, e)}
        >
          <Trash2 className="h-4 w-4" />
          <span className="sr-only">Delete</span>
        </Button>
      </div>
    )
  },
  ...(options?.showOrgExtraColumns
    ? [
        {
          key: "minAge" as const,
          header: "Min age",
          sortable: true,
          width: "w-[80px] min-w-[60px]",
          className: "p-2 text-muted-foreground",
          render: (offer: Offer) =>
            offer.variationData?.minAgeInYears ?? offer.variationData?.minAgeEffective ?? "–"
        },
        {
          key: "minGrade" as const,
          header: "Min grade",
          sortable: true,
          width: "w-[80px] min-w-[60px]",
          className: "p-2 text-muted-foreground",
          render: (offer: Offer) =>
            formatGrade(offer.variationData?.minAgeGradeKey)
        },
        {
          key: "maxAge" as const,
          header: "Max age",
          sortable: true,
          width: "w-[80px] min-w-[60px]",
          className: "p-2 text-muted-foreground",
          render: (offer: Offer) =>
            offer.variationData?.maxAgeInYears ?? offer.variationData?.maxAgeEffective ?? "–"
        },
        {
          key: "maxGrade" as const,
          header: "Max grade",
          sortable: true,
          width: "w-[80px] min-w-[60px]",
          className: "p-2 text-muted-foreground",
          render: (offer: Offer) =>
            formatGrade(offer.variationData?.maxAgeGradeKey)
        },
        {
          key: "tier2Price" as const,
          header: "Tier 2 price",
          sortable: true,
          width: "w-[90px] min-w-[70px]",
          className: "p-2 text-muted-foreground",
          render: (offer: Offer) => {
            // KB-2782: no fallback to the raw pricingTierPriceOverrides.tier2 input. priceTier2Calc
            // is the authoritative, VALIDATED tier price (withheld to null when it wasn't strictly
            // below the public price) — falling back to the raw operator-entered override here would
            // show an ascending/withheld price as if it were valid, defeating the guard for exactly
            // the audience (admins) who'd otherwise trust this column.
            //
            // Known transient case, accepted: the camp-data review queue can apply
            // pricingTierPriceOverrides and priceTier2Calc as two INDEPENDENT field-level approvals
            // (applyOfferFieldChangesCore.ts's APPLIABLE_OFFER_FIELDS) — an operator who approves only
            // the override, not its co-proposed tier2Calc recompute, leaves this column blank until
            // they approve the other. Blank-until-fully-approved is the safer failure than showing an
            // unvalidated number; it's also a nudge that the approval is incomplete.
            const v = offer.priceTier2Calc;
            return v != null ? `$${v}` : "–";
          }
        },
        {
          key: "sessionLength" as const,
          header: "Session length",
          sortable: true,
          width: "w-[100px] min-w-[80px]",
          className: "p-2 text-muted-foreground",
          render: (offer: Offer) => {
            // KB-2877 (review round 1, O2) — a WITHHELD length must not render as a confident
            // number. This fell through to `variationData.numDays` and showed "28 days" for a
            // session whose length the pipeline explicitly refused to derive — hiding the refusal
            // from the one person who can act on it, on the very surface a curator uses to decide
            // whether to set `numDaysOverride`. A curator's override still shows, because that is
            // a human answering the question rather than the fallback guessing it.
            if ((offer as any).numDaysState === "withheld" && (offer as any).numDaysOverride == null) {
              return "unknown";
            }
            const days =
              offer.numDaysCalc ??
              (offer as any).numDaysOverride ??
              offer.variationData?.numDays;
            return days != null && days !== "" ? `${days} day${days !== 1 ? "s" : ""}` : "–";
          }
        }
      ]
    : [])
];
